import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerSummaryDto, RenewalRobotAction } from '@bhops/shared';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { TimeSelect } from '@/components/TimeSelect';
import { Icon } from '@/components/Icon';
import { useOptionalToast } from '@/components/Toast';
import { SearchSelect } from '@/components/SearchSelect';
import { infrastructureOrdersApi } from '@/api/infrastructure-orders';
import { activeCustomersApi } from '@/api/active-customers';
import { customersApi } from '@/api/customers';
import { customerMasterApi } from '@/api/customer-master';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { extractApiError } from '@/api/client';
import { kstYmdHyphen } from '@/lib/format-date';
import { CATEGORY_META, type CalendarCategory } from './calendar-categories';
import { RenewalPlanList, type RenewalRobotRow } from './RenewalPlanList';
import { MaintenanceReportChoice, type ReportChoice } from './MaintenanceReportChoice';
import {
  EventScheduleFields,
  emptyEventFields,
  eventFieldsReady,
  eventRobotLines,
  eventExtras,
  type EventFieldsValue,
} from './EventScheduleFields';

// 2026-10-19 오너 요청 — "캘린더에서 날짜를 클릭하거나, 날짜 칸에 있는 흰색 영역을 클릭하면 신규
// 일정을 추가할 수 있게 하자. 유지보수, 회수 일정도 추가할 수 있게 해야해. 당연히 구글캘린더랑
// 연동 돼야해."
//
// 캘린더의 일정은 모두 콘솔 자료의 **날짜 칸**이다(따로 '일정' 표가 없다). 그래서 '일정 추가'는
// 그 날짜 칸을 채우는 일이다:
//   · 로봇 설치 · 그릴러 발주 · 그릴러 납품 → 인프라 발주 하나를 골라 그 날짜 칸
//     (통합 캘린더 끌어 옮기기와 같은 길 — 저장 규칙도 같다: 납품 ≥ 발주, 설치 ≥ 발주 + 영업일 5일)
//   · 유지보수 → 고객사 · 로봇을 골라 예방정비 서비스 오더를 새로 만든다(방문 예정 = 그날 10시)
//   · 로봇 교체(2026-10-20) → 고객사 · 뺄 로봇을 골라 '로봇 교체' 서비스 오더를 만든다(활성 고객사
//     '로봇 교체' 등록과 같은 길 — 제조팀에 교체 요청 알람, 넣을 로봇은 제조팀이 배정)
//   · 회수 → 고객사의 회수 예정일. 아직 '회수 예정'이 아니면 함께 바꾼다(현장 앱 로봇 회수 탭에
//     나오려면 그 상태여야 한다).
//   · 재계약(2026-10-22) → 옛 고객사(로봇이 나가 있는) · 새 고객사(계약 화면에서 만든 새 행, 예:
//     팔만식당[26년 10월]) · 옛 로봇마다 회수 / 이어서 사용. 계약은 여기서 만들지 않는다(오너).
//   · 이벤트(2026-11-02) → 맨 위 날짜 · 시간이 설치 일시. 회수 일시 · 장소 · 내용 · 출고할 로봇(모델 · 대수)
//     · 준비물. 로봇은 제조팀이 로봇 배정 화면에서 배정한다. 캘린더엔 [이벤트 설치] · [이벤트 회수] 두 일정.
// 구글 캘린더는 서버의 동기화가 1분 안에 이 날짜들로 일정을 만든다(google-calendar-script).

type NewKind =
  | 'install'
  | 'order'
  | 'delivery'
  | 'maintenance'
  | 'replace'
  | 'return'
  | 'renewal'
  | 'event';
const KINDS: NewKind[] = [
  'install',
  'order',
  'delivery',
  'maintenance',
  'replace',
  'return',
  'renewal',
  'event',
];
/** 고객사 + 로봇을 고르는 분류 — 유지보수 · 로봇 교체. */
const needsRobot = (k: NewKind) => k === 'maintenance' || k === 'replace';
const INFRA_FIELD = {
  install: 'installScheduledAt',
  order: 'grillOrderedAt',
  delivery: 'grillDeliveredAt',
} as const;
type InfraKind = keyof typeof INFRA_FIELD;
const isInfra = (k: NewKind): k is InfraKind => k in INFRA_FIELD;

interface Props {
  /** 누른 날짜(YYYY-MM-DD). 창 안에서 바꿀 수 있다. */
  date: string;
  onClose: () => void;
  /** 추가한 일정의 분류 — 캘린더가 꺼져 있던 분류를 켜서 바로 보이게 한다. */
  onAdded?: (category: CalendarCategory) => void;
}

/** 새 고객사 고르기 — 고객사 전체(한 번에 200개씩, 다 받을 때까지). */
async function listAllCustomers(): Promise<CustomerSummaryDto[]> {
  const out: CustomerSummaryDto[] = [];
  for (let page = 1; page <= 25; page += 1) {
    const r = await customersApi.list({ page, pageSize: 200 });
    out.push(...r.items);
    if (out.length >= r.total || r.items.length === 0) break;
  }
  return out;
}

const mmdd = (iso: string | null | undefined) => {
  const ymd = iso ? kstYmdHyphen(iso) : null;
  return ymd ? `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}` : null;
};

export function NewScheduleModal({ date: initialDate, onClose, onAdded }: Props) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [date, setDate] = useState(initialDate);
  // 2026-10-19 오너 — "시간도 설정 가능하게. 선택 항목. 안 고르면 하루 종일".
  const [time, setTime] = useState('');
  const [kind, setKind] = useState<NewKind>('install');
  const [infraId, setInfraId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [robotId, setRobotId] = useState('');
  // 2026-10-20 오너 — 로봇 교체는 여러 대를 한 일정으로(로봇마다 교체 오더 하나, 한 일정으로 묶인다).
  const [replaceIds, setReplaceIds] = useState<string[]>([]);
  const [memo, setMemo] = useState('');
  // 2026-11-02 오너 — 유지보수는 작업 메모와 따로 증상을 적는다(고객사 리포트의 요약이 된다).
  const [symptom, setSymptom] = useState('');
  // 2026-11-03 오너 — 유지보수는 그 고객사의 처리 안 된 리포트에 이을지, 새 리포트로 올릴지 고른다.
  const [reportChoice, setReportChoice] = useState<ReportChoice>('');
  useEffect(() => setReportChoice(''), [customerId]);
  const [markReturnPlanned, setMarkReturnPlanned] = useState(true);
  // 2026-10-22 — 재계약: 새 고객사 · 옛 로봇마다 회수(기본) / 이어서 사용.
  const [toCustomerId, setToCustomerId] = useState('');
  const [renewalPlan, setRenewalPlan] = useState<Record<string, RenewalRobotAction>>({});
  // 2026-11-02 — 이벤트: 회수 일시 · 장소 · 내용 · 출고할 로봇 · 준비물.
  const [eventFields, setEventFields] = useState<EventFieldsValue>(() =>
    emptyEventFields(initialDate),
  );
  // 설치 날짜를 회수 날짜 뒤로 옮기면 회수 날짜도 따라온다(회수 날짜를 앞으로 고친 것은 그대로 두고 알린다).
  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    setEventFields((v) => (v.returnDate < date ? { ...v, returnDate: date } : v));
  }, [date]);
  const [error, setError] = useState<string | null>(null);

  const infraQ = useQuery({
    queryKey: ['new-schedule', 'infra-orders'],
    queryFn: () => infrastructureOrdersApi.list({}),
    enabled: isInfra(kind),
    staleTime: 30_000,
  });
  const customersQ = useQuery({
    queryKey: ['new-schedule', 'active-customers'],
    queryFn: () => activeCustomersApi.list(),
    enabled: !isInfra(kind) && kind !== 'event',
    staleTime: 30_000,
  });

  const allCustomersQ = useQuery({
    queryKey: ['new-schedule', 'all-customers'],
    queryFn: listAllCustomers,
    enabled: kind === 'renewal',
    staleTime: 30_000,
  });

  const infraOptions = useMemo(() => {
    const rows = (infraQ.data ?? []).filter((o) => !(kind === 'install' && o.installCompletedAt));
    return [...rows].sort((a, b) =>
      (a.customer?.displayName ?? '').localeCompare(b.customer?.displayName ?? '', 'ko'),
    );
  }, [infraQ.data, kind]);
  const pickedInfra = infraOptions.find((o) => o.id === infraId) ?? null;

  const customerOptions = useMemo(() => {
    const rows = (customersQ.data?.items ?? []).filter(
      (c) =>
        c.robotsByModel.some((g) => g.robots.length > 0) || (kind === 'return' && c.returnPlanned),
    );
    return [...rows].sort((a, b) => a.displayName.localeCompare(b.displayName, 'ko'));
  }, [customersQ.data, kind]);
  const pickedCustomer = customerOptions.find((c) => c.customerId === customerId) ?? null;
  const robots = useMemo(
    () =>
      (pickedCustomer?.robotsByModel ?? []).flatMap((g) =>
        g.robots.map((r) => ({
          id: r.robotId,
          label: `${r.serialShort ? `#${r.serialShort}` : (r.serialLong ?? r.robotId)} · ${g.modelDisplayName}`,
        })),
      ),
    [pickedCustomer],
  );

  const renewalRobots = useMemo<RenewalRobotRow[]>(
    () =>
      (pickedCustomer?.robotsByModel ?? []).flatMap((g) =>
        g.robots.map((r) => ({
          robotId: r.robotId,
          label: r.serialShort ? `#${r.serialShort}` : (r.serialLong ?? r.robotId),
          modelName: g.modelDisplayName,
        })),
      ),
    [pickedCustomer],
  );
  // 새 고객사 — 옛 고객사 이름으로 시작하는 행(예: 팔만식당 → 팔만식당[26년 10월])을 맨 위에, 그중 가장
  // 최근에 만든 것부터. 띄어쓰기는 견주지 않는다('마루상회 [26년 10월]'도 '마루상회[26년 10월]'도).
  const toCandidates = useMemo(() => {
    const squash = (x: string) => x.replace(/\s+/g, '');
    const base = squash(pickedCustomer?.displayName ?? '');
    const suggested = (c: CustomerSummaryDto) => !!base && squash(c.displayName).startsWith(base);
    return (allCustomersQ.data ?? [])
      .filter((c) => c.id !== customerId)
      .map((c) => ({ c, s: suggested(c) }))
      .sort(
        (a, b) =>
          Number(b.s) - Number(a.s) ||
          (a.s ? b.c.createdAt.localeCompare(a.c.createdAt) : 0) ||
          a.c.displayName.localeCompare(b.c.displayName, 'ko'),
      );
  }, [allCustomersQ.data, customerId, pickedCustomer]);
  const toOptions = useMemo(
    () =>
      toCandidates.map(({ c, s }) => ({
        value: c.id,
        label: c.displayName,
        hint: s ? '추천' : undefined,
      })),
    [toCandidates],
  );
  // 2026-10-22 오너 — "마루상회를 골랐으면 새 계약건에 마루상회 [26년 10월]이 자동으로 들어가되, 다른
  // 선택지도 고를 수 있게". 옛 고객사를 고를 때마다 한 번만 채운다(사람이 바꾼 것은 덮지 않는다).
  const [autoFor, setAutoFor] = useState('');
  useEffect(() => {
    if (kind !== 'renewal' || !customerId || autoFor === customerId || !allCustomersQ.data) return;
    setAutoFor(customerId);
    const first = toCandidates[0];
    setToCustomerId(first?.s ? first.c.id : '');
  }, [kind, customerId, autoFor, allCustomersQ.data, toCandidates]);

  function pickKind(k: NewKind) {
    setKind(k);
    setError(null);
    // 고른 대상은 분류마다 다르다(인프라 발주 ↔ 고객사) — 바꾸면 비운다.
    if (isInfra(k) !== isInfra(kind)) {
      setInfraId('');
      setCustomerId('');
      setRobotId('');
      setReplaceIds([]);
      setToCustomerId('');
      setRenewalPlan({});
    }
  }

  const ready =
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    (isInfra(kind)
      ? !!pickedInfra
      : kind === 'event'
        ? eventFieldsReady(date, eventFields)
        : kind === 'replace'
          ? !!pickedCustomer && replaceIds.length > 0
          : kind === 'renewal'
            ? !!pickedCustomer && !!toCustomerId && toCustomerId !== customerId
            : kind === 'maintenance'
              ? !!pickedCustomer && replaceIds.length > 0 && reportChoice !== ''
              : !!pickedCustomer);

  const save = useMutation({
    mutationFn: async (): Promise<{
      alarm?: boolean;
      reportNo?: string;
      linked?: boolean;
    } | void> => {
      if (kind === 'event') {
        await scheduleDatesApi.createEvent({
          location: eventFields.location.trim(),
          content: eventFields.content.trim(),
          installDate: date,
          installTime: time || null,
          returnDate: eventFields.returnDate,
          returnTime: eventFields.returnTime || null,
          checklist: eventFields.checklist,
          robotLines: eventRobotLines(eventFields),
          ...eventExtras(eventFields),
        });
        return;
      }
      if (kind === 'renewal') {
        await scheduleDatesApi.createRenewal({
          fromCustomerId: customerId,
          toCustomerId,
          date,
          time: time || null,
          ...(memo.trim() ? { memo: memo.trim() } : {}),
          plan: renewalRobots.map((r) => ({
            robotId: r.robotId,
            action: renewalPlan[r.robotId] ?? 'RETURN',
          })),
        });
        return;
      }
      if (kind === 'replace') {
        // 서버가 '로봇 교체' 서비스 오더를 만들고 제조팀에 교체 요청 알람을 보낸다.
        const r = await scheduleDatesApi.createReplacement({
          customerId,
          robotIds: replaceIds,
          date,
          time: time || null,
          ...(memo.trim() ? { memo: memo.trim() } : {}),
        });
        return { alarm: r.replacementAlarmSent };
      }
      if (isInfra(kind)) {
        await scheduleDatesApi.moveInfra(infraId, kind, date, time || null);
      } else if (kind === 'maintenance') {
        // 서버가 예방정비 서비스 오더 + 고객사 리포트(진행중 · 번호 자동, 2026-11-02 — 종전엔 고장 조치 이력)를
        // 함께 만든다. 2026-10-22 — 여러 대면 로봇마다 오더, 한 일정 · 리포트 하나로 묶인다.
        const r = await scheduleDatesApi.createMaintenance({
          customerId,
          robotIds: replaceIds,
          date,
          time: time || null,
          ...(memo.trim() ? { memo: memo.trim() } : {}),
          ...(reportChoice !== 'new' && reportChoice !== ''
            ? { linkReportId: reportChoice }
            : symptom.trim()
              ? { symptom: symptom.trim() }
              : {}),
        });
        return { reportNo: r.reportNo, linked: r.linked };
      } else {
        if (pickedCustomer && !pickedCustomer.returnPlanned && markReturnPlanned) {
          await customerMasterApi.updateStatus(
            customerId,
            'return_planned',
            '통합 캘린더에서 회수 일정 등록',
          );
        }
        await scheduleDatesApi.moveReturn(customerId, date, time || null);
      }
    },
    onSuccess: async (res) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['infrastructure-orders'] }),
        qc.invalidateQueries({ queryKey: ['new-schedule'] }),
        qc.invalidateQueries({ queryKey: ['fault-remediations'] }),
        qc.invalidateQueries({ queryKey: ['customer-sheet-reports'] }),
        qc.invalidateQueries({ queryKey: ['assignments'] }),
      ]);
      const label = CATEGORY_META[kind].label;
      const when = `${date} ${time || '하루 종일'}`;
      toast?.success(
        kind === 'maintenance'
          ? res?.linked
            ? `${when} ${label} 일정을 추가했습니다. 고객사 리포트 ${res.reportNo}에 방문 일정을 이었고, 1분 안에 구글 캘린더에도 올라갑니다.`
            : `${when} ${label} 일정을 추가했습니다. 고객사 리포트${res?.reportNo ? `(${res.reportNo})` : ''}에도 올렸고, 1분 안에 구글 캘린더에도 올라갑니다.`
          : kind === 'replace'
            ? `${when} ${label} 일정을 추가했습니다.${res?.alarm ? ' 제조팀에 교체 요청을 보냈고,' : ''} 1분 안에 구글 캘린더에도 올라갑니다.`
            : kind === 'renewal'
              ? `${when} ${label} 일정을 추가했습니다. [재계약 완료]를 누르거나 새 고객사의 설치 서명을 받을 때까지 미완료로 남습니다.`
              : kind === 'event'
                ? `${when} 이벤트를 추가했습니다. 출고할 로봇은 제조팀이 로봇 배정 화면에서 배정하고, 1분 안에 구글 캘린더에 이벤트 설치와 이벤트 회수 일정으로 올라갑니다.`
                : `${when} ${label} 일정을 추가했습니다. 1분 안에 구글 캘린더에도 올라갑니다.`,
      );
      onAdded?.(kind);
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  const existing = isInfra(kind)
    ? mmdd(pickedInfra?.[INFRA_FIELD[kind]] as string | null | undefined)
    : kind === 'return'
      ? mmdd(pickedCustomer?.returnScheduledAt)
      : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="일정 추가"
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!ready || save.isPending}
            data-testid="new-schedule-save"
          >
            {save.isPending ? '추가 중…' : '추가'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ ...field, flex: '1 1 150px' }}>
            <span style={labelSt}>{kind === 'event' ? '설치 날짜' : '날짜'}</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={input}
              data-testid="new-schedule-date"
            />
          </label>
          <label style={{ ...field, flex: '2 1 260px' }}>
            <span style={labelSt}>
              {kind === 'event' ? '설치 시간' : '시간'}{' '}
              <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>(선택)</span>
            </span>
            {/* 2026-10-22 오너 — [지우기] 버튼 · 아래 안내 문구를 뺐다(누르다 고른 시간이 날아갔다).
                시간을 비우려면 첫 칸을 '선택'으로. */}
            <TimeSelect
              value={time}
              onChange={setTime}
              style={input}
              ariaLabel={kind === 'event' ? '설치 시간 (선택)' : '시간 (선택)'}
              testId="new-schedule-time"
            />
          </label>
        </div>

        <div style={field}>
          <span style={labelSt}>종류</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="radiogroup">
            {KINDS.map((k) => {
              const m = CATEGORY_META[k];
              const on = k === kind;
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => pickKind(k)}
                  data-testid={`new-schedule-kind-${k}`}
                  style={{
                    ...kindChip,
                    background: on ? m.bg : 'transparent',
                    color: on ? m.color : 'var(--fg-muted)',
                    borderColor: on ? m.color : 'var(--border-default)',
                  }}
                >
                  <Icon name={m.iconName} size={14} aria-hidden /> {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {isInfra(kind) ? (
          <div style={field}>
            <span style={labelSt}>인프라 발주</span>
            <SearchSelect
              value={infraId}
              onChange={setInfraId}
              loading={infraQ.isLoading}
              error={infraQ.isError}
              placeholder="고객사 이름이나 계약 번호로 검색"
              aria-label="인프라 발주"
              options={infraOptions.map((o) => {
                const cur = mmdd(o[INFRA_FIELD[kind]] as string | null | undefined);
                return {
                  value: o.id,
                  label: `${o.customer?.displayName ?? '고객사'}${o.contract?.humanId ? ` · ${o.contract.humanId}` : ''}`,
                  hint: cur ? `지금 ${cur}` : undefined,
                };
              })}
              data-testid="new-schedule-infra"
            />
            {!infraQ.isLoading && infraOptions.length === 0 && (
              <span style={hint}>
                고를 인프라 발주가 없습니다. 새 고객사는 [+ 신규 발주]로 먼저 만들어 주세요.
              </span>
            )}
          </div>
        ) : kind === 'event' ? (
          <EventScheduleFields installDate={date} value={eventFields} onChange={setEventFields} />
        ) : (
          <div style={field}>
            <span style={labelSt}>{kind === 'renewal' ? '계약을 마감할 옛 고객사' : '고객사'}</span>
            <SearchSelect
              value={customerId}
              onChange={(v) => {
                setCustomerId(v);
                setRobotId('');
                setReplaceIds([]);
                setRenewalPlan({});
                if (v === toCustomerId) setToCustomerId('');
              }}
              loading={customersQ.isLoading}
              error={customersQ.isError}
              placeholder="로봇이 나가 있는 고객사를 이름으로 검색"
              aria-label="고객사"
              options={customerOptions.map((c) => ({
                value: c.customerId,
                label: c.displayName,
                hint:
                  kind === 'return' && c.returnScheduledAt
                    ? `지금 ${mmdd(c.returnScheduledAt)}`
                    : undefined,
              }))}
              data-testid="new-schedule-customer"
            />
          </div>
        )}

        {needsRobot(kind) && pickedCustomer && (
          <>
            {kind === 'replace' ? (
              <div style={field} role="group" aria-label="회수할 로봇">
                <span style={labelSt}>
                  회수할 로봇{' '}
                  <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>
                    {replaceIds.length > 0
                      ? `${replaceIds.length}대를 골랐습니다`
                      : '여러 대 고를 수 있습니다'}
                  </span>
                </span>
                <div style={robotList}>
                  {robots.map((r) => (
                    <label key={r.id} style={robotRow}>
                      <input
                        type="checkbox"
                        checked={replaceIds.includes(r.id)}
                        onChange={(e) =>
                          setReplaceIds((prev) =>
                            e.target.checked ? [...prev, r.id] : prev.filter((x) => x !== r.id),
                          )
                        }
                        data-testid={`new-schedule-replace-robot-${r.id}`}
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
                <span style={hint} data-testid="new-schedule-replace-hint">
                  설치할 로봇은 제조팀이 로봇마다 배정하며, 제조팀에 교체 요청 알림이 갑니다. 일정을
                  눌러 [교체 완료]를 할 때 골라도 됩니다.
                </span>
              </div>
            ) : (
              // 2026-10-22 오너 — "유지보수 대상 로봇이 여러 대인 경우도 있는데, 한 대밖에 선택을 못 함".
              <div style={field} role="group" aria-label="유지보수할 로봇">
                <span style={labelSt}>
                  로봇{' '}
                  <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>
                    {replaceIds.length > 0
                      ? `${replaceIds.length}대를 골랐습니다`
                      : '여러 대 고를 수 있습니다'}
                  </span>
                </span>
                <div style={robotList}>
                  {robots.map((r) => (
                    <label key={r.id} style={robotRow}>
                      <input
                        type="checkbox"
                        checked={replaceIds.includes(r.id)}
                        onChange={(e) =>
                          setReplaceIds((prev) =>
                            e.target.checked ? [...prev, r.id] : prev.filter((x) => x !== r.id),
                          )
                        }
                        data-testid={`new-schedule-robot-${r.id}`}
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {kind === 'maintenance' && (
              <MaintenanceReportChoice
                customerId={customerId}
                value={reportChoice}
                onChange={setReportChoice}
              />
            )}
            {kind === 'maintenance' && (reportChoice === 'new' || reportChoice === '') && (
              <label style={field}>
                <span style={labelSt}>증상 (선택)</span>
                <textarea
                  value={symptom}
                  onChange={(e) => setSymptom(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder="예) 회전할 때 걸림, 전면 LED 패널 파손"
                  style={{ ...input, height: 'auto', resize: 'vertical' }}
                  data-testid="new-schedule-symptom"
                />
                <span style={hint}>
                  고객사 리포트의 요약으로 올라갑니다. 비워 두면 작업 메모가 대신 들어갑니다.
                </span>
              </label>
            )}
            <label style={field}>
              <span style={labelSt}>작업 메모 (선택)</span>
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder={
                  kind === 'replace'
                    ? '예) 그릴 모터 소음이 있어 새 로봇으로 교체'
                    : '예) 정기 점검, 필터 교체'
                }
                data-testid="new-schedule-memo"
                style={{ ...input, height: 'auto', resize: 'vertical' }}
              />
            </label>
          </>
        )}

        {kind === 'renewal' && pickedCustomer && (
          <>
            <div style={field}>
              <span style={labelSt}>새 계약을 맺은 고객사</span>
              <SearchSelect
                value={toCustomerId}
                onChange={setToCustomerId}
                loading={allCustomersQ.isLoading}
                error={allCustomersQ.isError}
                placeholder="이름으로 검색, 예) 팔만식당[26년 10월]"
                aria-label="새 고객사"
                options={toOptions}
                data-testid="new-schedule-renewal-to"
              />
              <span style={hint}>
                새 계약과 새 고객사는 계약 화면에서 먼저 만들어 주세요. 새로 설치할 로봇은 배정
                인박스에서 새 계약에 배정합니다.
              </span>
            </div>
            <div style={field}>
              <span style={labelSt}>{pickedCustomer.displayName} 로봇</span>
              <RenewalPlanList
                robots={renewalRobots}
                plan={renewalPlan}
                onChange={(id, a) => setRenewalPlan((p) => ({ ...p, [id]: a }))}
                testId="new-schedule-renewal-plan"
              />
            </div>
            <label style={field}>
              <span style={labelSt}>메모 (선택)</span>
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="예) 오전에 회수하고 오후에 새 로봇 설치"
                style={{ ...input, height: 'auto', resize: 'vertical' }}
              />
            </label>
            <span style={hint} data-testid="new-schedule-renewal-hint">
              날짜가 지나도 저절로 끝나지 않습니다. 일정을 눌러 [재계약 완료]를 누르거나 새 고객사의
              설치 서명을 받으면 끝납니다.
            </span>
          </>
        )}

        {kind === 'return' && pickedCustomer && !pickedCustomer.returnPlanned && (
          <label style={checkRow}>
            <input
              type="checkbox"
              checked={markReturnPlanned}
              onChange={(e) => setMarkReturnPlanned(e.target.checked)}
              data-testid="new-schedule-mark-return"
            />
            고객사 상태를 &lsquo;회수 예정&rsquo;으로 바꿉니다. 현장 앱의 &lsquo;로봇 회수&rsquo;
            탭에 나옵니다.
          </label>
        )}

        {existing && (
          <div style={note} data-testid="new-schedule-existing">
            지금 {existing}에 잡혀 있습니다. 추가하면 {mmdd(`${date}T00:00:00.000Z`)}로 옮깁니다.
          </div>
        )}

        {error && (
          <div role="alert" style={errorSt} data-testid="new-schedule-error">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelSt: CSSProperties = { fontSize: 13, fontWeight: 600 };
const input: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const kindChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const hint: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)' };
const checkRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  cursor: 'pointer',
};
const robotList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  maxHeight: 180,
  overflowY: 'auto',
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
};
const robotRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  cursor: 'pointer',
  padding: '2px 0',
};
const note: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-sunken)',
  fontSize: 13,
};
const errorSt: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  fontSize: 13,
};
